import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { Input } from '../ui/Input'
import { UnifiedSettingsDialog } from '@/components/shared/UnifiedSettingsDialog'
import { PlaybookShareDialog } from '@/components/shared/PlaybookShareDialog'
import { AnimationDialog } from '@/components/animation/AnimationDialog'
import { SlideExportDialog } from '@/components/export/SlideExportDialog'
import { ConfirmationDialogRenderer } from './ConfirmationDialogRenderer'
import type { ExportSection } from './Types'
import type { ConfirmationConfig } from '@/hooks/UseConfirmation'
import type { Play } from './Types'

type ModalName = 'settings' | 'share' | 'rename' | 'export' | 'playViewer'

interface ModalManager {
	isOpen: (name: ModalName) => boolean
	close: (name: ModalName) => void
}

interface UseConfirmationReturn<T> {
	item: T | null
	isOpen: boolean
	isConfirming: boolean
	prompt: (itemToConfirm: T) => void
	confirm: () => Promise<void>
	cancel: () => void
	action: () => Promise<void>
	getDescription: () => string
	config: ConfirmationConfig<T>
}

interface PlaybookEditorDialogsProps {
	// IDs
	playbookId: string | undefined
	teamId: string | undefined

	// Names
	playbookName: string

	// Modal manager
	modals: ModalManager

	// Data
	exportSections: ExportSection[]
	allPlays: Play[]

	// State
	renamePlayName: string
	viewingPlayId: string | null

	// State setters
	onRenamePlayNameChange: (name: string) => void

	// Actions
	onConfirmRename: () => void

	// Confirmation dialogs
	deletePlayConfirm: UseConfirmationReturn<string>
	permanentDeleteConfirm: UseConfirmationReturn<string[]>
	deleteConceptConfirm: UseConfirmationReturn<{ id: string; type: 'concept' | 'formation' }>
}

export function PlaybookEditorDialogs({
	playbookId,
	teamId,
	playbookName,
	modals,
	exportSections,
	allPlays,
	renamePlayName,
	viewingPlayId,
	onRenamePlayNameChange,
	onConfirmRename,
	deletePlayConfirm,
	permanentDeleteConfirm,
	deleteConceptConfirm,
}: PlaybookEditorDialogsProps) {
	return (
		<>
			{/* Delete Concept Confirmation */}
			<ConfirmationDialogRenderer confirmation={deleteConceptConfirm} />

			{/* Settings Dialog */}
			<UnifiedSettingsDialog
				isOpen={modals.isOpen('settings')}
				onClose={() => modals.close('settings')}
				context="playbook-editor"
				playbookId={playbookId}
			/>

			{/* Share Dialog */}
			<PlaybookShareDialog
				isOpen={modals.isOpen('share')}
				onClose={() => modals.close('share')}
				playbookName={playbookName}
				playbookId={playbookId ?? ''}
				currentTeamId={teamId ?? null}
			/>

			{/* Export Dialog */}
			<SlideExportDialog
				isOpen={modals.isOpen('export')}
				onClose={() => modals.close('export')}
				sections={exportSections}
				playbookName={playbookName}
				playbookId={playbookId || ''}
			/>

			{/* Rename Play Dialog */}
			<Dialog
				open={modals.isOpen('rename')}
				onOpenChange={(open) => !open && modals.close('rename')}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Rename Play</DialogTitle>
					</DialogHeader>
					<div className="playbook-editor-modal-content">
						<div>
							<label className="playbook-editor-modal-label">Play Name</label>
							<Input
								type="text"
								value={renamePlayName}
								onChange={(e) => onRenamePlayNameChange(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										onConfirmRename()
									}
								}}
								placeholder="Enter play name..."
								autoFocus
							/>
						</div>
						<div className="playbook-editor-modal-actions">
							<button
								onClick={() => modals.close('rename')}
								className="playbook-editor-modal-button playbook-editor-modal-button--secondary"
							>
								Cancel
							</button>
							<button
								onClick={onConfirmRename}
								disabled={!renamePlayName.trim()}
								className="playbook-editor-modal-button playbook-editor-modal-button--primary"
							>
								Rename
							</button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Delete Play Confirmation */}
			<ConfirmationDialogRenderer confirmation={deletePlayConfirm} />

			{/* Permanent Delete Plays Confirmation */}
			<ConfirmationDialogRenderer confirmation={permanentDeleteConfirm} />

			{/* Play Animation Dialog */}
			<AnimationDialog
				isOpen={modals.isOpen('playViewer')}
				onClose={() => modals.close('playViewer')}
				playId={viewingPlayId}
				playName={allPlays.find(p => p.id === viewingPlayId)?.name}
			/>
		</>
	)
}
